import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Typography from '@mui/material/Typography/Typography';
import Alert from '@mui/material/Alert/Alert';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Chip from '@mui/material/Chip/Chip';
import Paper from '@mui/material/Paper/Paper';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { alpha, useTheme } from '@mui/material/styles';
import { ACCEPTED_IMAGE_TYPES, Branding, BRANDING_FONTS, resolveBranding, uploadBrandAsset } from '../../services/branding.service';
import { DEFAULT_ACCENT, DEFAULT_PRIMARY, ensureReadableOnWhite, isLowContrast } from '../../theme';
import { BrandedThemeProvider } from '../Theme/AppThemeProvider';
import { Logo } from '../logo/icon';
import { useAuth } from '../../services/useAuth';
import { imageToDataUrl, isLocalImage } from '../../services/draftImages';
import { HackathonBanner } from '../Hackathon/HackathonBanner';

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
const HEX = /^#[0-9a-f]{6}$/i;

const InheritedHint = ({ show, from }: { show: boolean, from?: string }) => {
    if (!show) {
        return null;
    }
    return <Chip size="small" variant="outlined" label={from ? `Inherited from ${from}` : 'Inherited'} />;
}

const ImageField = ({
    label,
    helperText,
    value,
    inherited,
    inheritedFrom,
    wide,
    onChange
}: {
    label: string,
    helperText: string,
    value?: string,
    inherited?: string,
    inheritedFrom?: string,
    wide?: boolean,
    onChange: (url: string) => void
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [showUrl, setShowUrl] = useState(false);
    // uploads are stored against an account; signed out (setting up a draft), the image is kept in the draft until then
    const { user, loading } = useAuth();
    const signedOut = !loading && !user;

    const shown = value || inherited;

    const upload = (file: File) => {
        setError('');
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            setError('Use a PNG, JPEG, GIF or WebP image.');
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            setError('Images must be 2MB or smaller.');
            return;
        }
        setUploading(true);
        // a logo shows small; a banner spans the page
        const store = signedOut ? imageToDataUrl(file, wide ? 1600 : 512) : uploadBrandAsset(file);
        store.then(url => {
            onChange(url);
        }, (err) => {
            setError(err?.response?.data?.message || (signedOut ? "Couldn't read that image" : 'Upload failed'));
        }).finally(() => {
            setUploading(false);
        });
    }

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">{label}</Typography>
                <InheritedHint show={!value && !!inherited} from={inheritedFrom} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{
                    width: wide ? 160 : 72,
                    height: 72,
                    flexShrink: 0,
                    borderRadius: '10px',
                    border: theme => `1px dashed ${theme.palette.divider}`,
                    backgroundColor: 'var(--surface-muted)',
                    backgroundImage: shown && wide ? `url("${shown.replace(/"/g, '%22')}")` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                }}>
                    {
                        uploading ? <CircularProgress size={24} /> : (
                            shown && !wide && <Box component="img" src={shown} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        )
                    }
                </Box>
                <Stack spacing={1} alignItems="flex-start">
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" startIcon={<CloudUploadIcon />} disabled={uploading} onClick={() => inputRef.current?.click()}>
                            Upload
                        </Button>
                        <Button size="small" onClick={() => setShowUrl(!showUrl)}>Use a URL</Button>
                        {value && <Button size="small" color="inherit" onClick={() => onChange('')}>{inherited ? 'Use inherited' : 'Remove'}</Button>}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{helperText}</Typography>
                </Stack>
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    hidden
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            upload(file);
                        }
                        e.target.value = '';
                    }} />
            </Stack>
            {
                showUrl && (
                    <TextField
                        size="small"
                        label={`${label} URL`}
                        placeholder="https://"
                        // an image kept in the draft isn't a link to show or edit
                        value={isLocalImage(value) ? '' : value || ''}
                        onChange={(e) => onChange(e.target.value)} />
                )
            }
            {error && <Alert severity="error">{error}</Alert>}
        </Stack>
    )
}

const ColorField = ({
    label,
    value,
    inherited,
    inheritedFrom,
    fallback,
    onChange
}: {
    label: string,
    value?: string,
    inherited?: string,
    inheritedFrom?: string,
    fallback: string,
    onChange: (color: string) => void
}) => {
    const [text, setText] = useState(value || '');

    useEffect(() => {
        setText(value || '');
    }, [value]);

    const effective = value || inherited || fallback;
    const adjusted = ensureReadableOnWhite(effective);

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">{label}</Typography>
                <InheritedHint show={!value && !!inherited} from={inheritedFrom} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <Box component="input"
                    type="color"
                    aria-label={label}
                    value={effective}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                    sx={{
                        width: 44,
                        height: 40,
                        p: 0.5,
                        border: theme => `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                    }} />
                <TextField
                    size="small"
                    sx={{ width: 140 }}
                    placeholder={inherited || fallback}
                    value={text}
                    error={!!text && !HEX.test(text)}
                    onChange={(e) => {
                        const next = e.target.value.trim();
                        setText(next);
                        if (!next || HEX.test(next)) {
                            onChange(next.toLowerCase());
                        }
                    }} />
                {value && <Button size="small" color="inherit" onClick={() => onChange('')}>Reset</Button>}
            </Stack>
            {
                isLowContrast(effective) && (
                    <Alert severity="info" sx={{ py: 0 }}>
                        This color is light, so buttons and links will use a darker shade (<b>{adjusted}</b>) to stay readable. Banners keep the original.
                    </Alert>
                )
            }
        </Stack>
    )
}

// sample event for the preview: ideas open for a few more days, a handful of people
const PREVIEW_PEOPLE = ['Ana', 'Ben', 'Chen', 'Dev', 'Eli', 'Fay', 'Gus'].map(username => ({ username, email: `${username.toLowerCase()}@example.com` }));

const BrandingPreview = ({ branding, name }: { branding: Branding, name: string }) => {
    return (
        <BrandedThemeProvider branding={branding}>
            <PreviewBody name={name} />
        </BrandedThemeProvider>
    )
}

const PreviewBody = ({ name }: { name: string }) => {
    const theme = useTheme();
    // a fixed few days out, so the countdown tile reads the same while the preview is open
    const [sampleDeadline] = useState(() => new Date(Date.now() + 3 * 24 * 60 * 60 * 1000));
    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: '14px', backgroundColor: theme.palette.background.default }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{
                px: 2,
                py: 1.25,
                // as the site header: a branded event washes it with the brand colour
                background: theme.brand.isCustom ? `linear-gradient(${alpha(theme.brand.rawPrimary, 0.1)}, ${alpha(theme.brand.rawPrimary, 0.1)}), #fff` : '#fff',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                {/* the site's own header: the logo uploaded here shows in the banner, not in place of ours */}
                <Logo size="xsmall" />
                <Typography variant="subtitle2" sx={{ fontFamily: theme.typography.h6.fontFamily }}>
                    Hack in a Box
                </Typography>
            </Stack>
            <Stack spacing={2} sx={{ p: 2 }}>
                <HackathonBanner
                    compact
                    name={name || 'Your hackathon'}
                    organizationName={theme.brand.displayName}
                    status={{ phase: 'ideas-open', label: 'Accepting ideas', color: 'success' }}
                    dateRange="Nov 6 - 7"
                    deadline={sampleDeadline}
                    people={PREVIEW_PEOPLE}
                    ideaCount={12}
                    teamPrompt={{ title: "You're not on a team yet", subtitle: '3 ideas are looking for people', actionLabel: 'Browse', onAction: () => undefined }} />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button variant="contained" size="small">Join team</Button>
                    <Button variant="outlined" size="small">Submit an idea</Button>
                    <Chip size="small" color="secondary" label="Accent" />
                    <Typography variant="body2"><Box component="a" href="#" onClick={(e: React.MouseEvent) => e.preventDefault()} sx={{ color: 'primary.main' }}>A link</Box></Typography>
                </Stack>
            </Stack>
        </Paper>
    )
}

/**
 * Edits a branding object. `inherited` is the organization's branding, shown as defaults that empty
 * fields fall back to.
 */
export const BrandingEditor = React.memo(({
    value,
    onChange,
    inherited,
    inheritedFrom,
    previewName
}: {
    value: Branding,
    onChange: (branding: Branding) => void,
    inherited?: Branding,
    inheritedFrom?: string,
    previewName: string
}) => {
    // images finish uploading later: build on the latest branding, or a logo and banner landing close together
    // would each overwrite the other with the branding from before either
    const latest = useRef(value);
    latest.current = value;
    const set = (key: keyof Branding, fieldValue: string) => {
        latest.current = { ...latest.current, [key]: fieldValue };
        onChange(latest.current);
    }

    return (
        <Box sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            alignItems: 'start'
        }}>
            <Stack spacing={3}>
                <TextField
                    label="Display name"
                    helperText="Shown in the banner, e.g. your company or team name"
                    placeholder={inherited?.displayName}
                    value={value.displayName || ''}
                    onChange={(e) => set('displayName', e.target.value)} />
                <ImageField
                    label="Logo"
                    helperText="Square or wide, transparent PNG works best. Max 2MB."
                    value={value.logoUrl}
                    inherited={inherited?.logoUrl}
                    inheritedFrom={inheritedFrom}
                    onChange={(url) => set('logoUrl', url)} />
                <ImageField
                    wide
                    label="Banner image"
                    helperText="Wide image, ideally 1600×500 or larger. Max 2MB."
                    value={value.bannerUrl}
                    inherited={inherited?.bannerUrl}
                    inheritedFrom={inheritedFrom}
                    onChange={(url) => set('bannerUrl', url)} />
                <Stack spacing={3}>
                    <ColorField
                        label="Primary color"
                        value={value.primaryColor}
                        inherited={inherited?.primaryColor}
                        inheritedFrom={inheritedFrom}
                        fallback={DEFAULT_PRIMARY}
                        onChange={(color) => set('primaryColor', color)} />
                    <ColorField
                        label="Accent color"
                        value={value.accentColor}
                        inherited={inherited?.accentColor}
                        inheritedFrom={inheritedFrom}
                        fallback={DEFAULT_ACCENT}
                        onChange={(color) => set('accentColor', color)} />
                </Stack>
                <TextField
                    select
                    label="Font"
                    value={value.fontFamily || ''}
                    helperText={!value.fontFamily && inherited?.fontFamily ? `Inherited: ${inherited.fontFamily}` : undefined}
                    onChange={(e) => set('fontFamily', e.target.value)}>
                    <MenuItem value="">{inherited?.fontFamily ? `Inherit (${inherited.fontFamily})` : 'Default (your device\'s fonts)'}</MenuItem>
                    {
                        BRANDING_FONTS.map(font => (
                            <MenuItem key={font} value={font}>{font}</MenuItem>
                        ))
                    }
                </TextField>
            </Stack>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
                <Typography variant="overline" color="text.secondary">Preview</Typography>
                <BrandingPreview branding={resolveBranding(inherited, value)} name={previewName} />
            </Box>
        </Box>
    )
});
